import { createFileRoute } from '@tanstack/react-router';
import { ShieldCheck } from 'lucide-react';
import { useState } from 'react';

import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { BusinessTab } from '@/features/control/components/business-tab';
import { EvalsTab } from '@/features/control/components/evals-tab';
import { FolioGapsTab } from '@/features/control/components/folio-gaps-tab';
import { SummaryTab } from '@/features/control/components/summary-tab';
import { SystemTab } from '@/features/control/components/system-tab';
import { TeamTab } from '@/features/control/components/team-tab';
import { useAlertCount } from '@/features/control/hooks/use-evals';

// Centro de Control — vista exclusiva del owner. 5 tabs:
//   Resumen (default) · Equipo · Negocio · Sistema · Evals
//
// El tab Onboarding del sprint anterior se removió de la barra (su data
// sigue accesible en cliente 360°). Para reactivarlo, reimportar
// OnboardingTab y volver a agregar el TabsTrigger.

type TabKey = 'resumen' | 'equipo' | 'negocio' | 'sistema' | 'evals' | 'folios';

export const Route = createFileRoute('/_app/control/')({
  component: ControlPage,
});

function ControlPage() {
  const [tab, setTab] = useState<TabKey>('resumen');
  const alertCount = useAlertCount();
  const failCount = alertCount.data?.fail ?? 0;

  return (
    <div className="flex flex-col gap-6">
      <header className="flex items-start gap-3">
        <span
          className="flex h-10 w-10 items-center justify-center rounded-lg"
          style={{
            background:
              'linear-gradient(135deg, color-mix(in srgb, var(--color-primary) 20%, transparent), color-mix(in srgb, var(--color-primary) 8%, transparent))',
            border: '1px solid color-mix(in srgb, var(--color-primary) 30%, transparent)',
          }}
          aria-hidden
        >
          <ShieldCheck className="h-5 w-5" style={{ color: 'var(--color-primary)' }} />
        </span>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-semibold tracking-tight">Centro de Control</h1>
            <span
              className="rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider"
              style={{
                background: 'color-mix(in srgb, var(--color-primary) 12%, transparent)',
                color: 'var(--color-primary)',
                border: '1px solid color-mix(in srgb, var(--color-primary) 30%, transparent)',
              }}
            >
              Owner
            </span>
          </div>
          <p className="mt-0.5 text-sm" style={{ color: 'var(--color-fg-muted)' }}>
            Vista exclusiva del owner — resumen, equipo, métricas del negocio y health del sistema.
          </p>
        </div>
      </header>

      <Tabs value={tab} onValueChange={(v) => setTab(v as TabKey)}>
        <TabsList>
          <TabsTrigger value="resumen">Resumen</TabsTrigger>
          <TabsTrigger value="equipo">Equipo</TabsTrigger>
          <TabsTrigger value="negocio">Negocio</TabsTrigger>
          <TabsTrigger value="sistema">Sistema</TabsTrigger>
          <TabsTrigger value="evals" className="relative">
            <span>Evals y pipeline</span>
            {failCount > 0 ? (
              <span
                className="ml-2 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full px-1 text-[10px] font-semibold tabular-nums text-white"
                style={{ background: '#E24B4A' }}
              >
                {failCount > 99 ? '99+' : failCount}
              </span>
            ) : null}
          </TabsTrigger>
          <TabsTrigger value="folios">Folios SII</TabsTrigger>
        </TabsList>
        <TabsContent value="resumen">
          <SummaryTab />
        </TabsContent>
        <TabsContent value="equipo">
          <TeamTab />
        </TabsContent>
        <TabsContent value="negocio">
          <BusinessTab />
        </TabsContent>
        <TabsContent value="sistema">
          <SystemTab />
        </TabsContent>
        <TabsContent value="evals">
          <EvalsTab />
        </TabsContent>
        <TabsContent value="folios">
          <FolioGapsTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
